
import { calculateTextEntropy } from '../textUtils';

/**
 * Advanced text feature extraction for improved detection accuracy
 */
export interface TextFeatures {
  entropies: {
    shannon: number;
    normalized: number;
    wordLevel: number;
  };
  complexity: {
    readability: number;
    sentenceVariability: number;
    vocabRichness: number;
  };
  patterns: {
    repetition: number;
    formulaic: number;
    transition: number;
  };
  metadata: {
    [key: string]: any;
  };
}

/**
 * Extract rich text features for advanced analysis
 * @param text The text to analyze
 * @returns Object containing extracted features
 */
export function extractAdvancedFeatures(text: string): TextFeatures {
  // Basic sanity check
  if (!text || text.length < 10) {
    return createEmptyFeatures();
  }
  
  const words = text.toLowerCase().split(/\s+/).filter(w => w.length > 0);
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
  
  // Calculate Shannon entropy (character-level)
  const shannonEntropy = calculateTextEntropy(text);
  
  // Calculate normalized entropy (0-1 scale)
  const idealEnglishEntropy = 4.2; // Typical value for English text
  const normalizedEntropy = Math.min(1, shannonEntropy / idealEnglishEntropy);
  
  // Word-level entropy calculation
  const wordFrequencies = getWordFrequencies(words);
  const wordLevelEntropy = calculateWordEntropy(wordFrequencies, words.length);
  
  // Readability metrics (simplified Flesch-Kincaid)
  const avgSentenceLength = words.length / Math.max(1, sentences.length);
  const avgWordLength = text.replace(/[^a-zA-Z]/g, '').length / Math.max(1, words.length);
  const readabilityScore = 0.39 * avgSentenceLength + 11.8 * avgWordLength - 15.59;
  
  // Sentence variability
  const sentenceLengths = sentences.map(s => s.split(/\s+/).filter(w => w.length > 0).length);
  const sentenceVariability = calculateVariability(sentenceLengths);
  
  // Vocabulary richness
  const uniqueWords = new Set(words).size;
  const vocabRichness = uniqueWords / Math.max(1, words.length);
  
  // Repetition patterns
  const repetitionScore = detectRepetitionPatterns(text);
  
  // Formulaic phrases detection
  const formulaicScore = detectFormulaicPhrases(text);
  
  // Transition words analysis
  const transitionScore = analyzeTransitionWords(text);
  
  return {
    entropies: {
      shannon: shannonEntropy,
      normalized: normalizedEntropy,
      wordLevel: wordLevelEntropy
    },
    complexity: {
      readability: readabilityScore,
      sentenceVariability: sentenceVariability,
      vocabRichness: vocabRichness
    },
    patterns: {
      repetition: repetitionScore,
      formulaic: formulaicScore,
      transition: transitionScore
    },
    metadata: {
      wordCount: words.length,
      sentenceCount: sentences.length,
      paragraphCount: paragraphs.length,
      avgSentenceLength: avgSentenceLength,
      avgWordLength: avgWordLength
    }
  };
}

/**
 * Create empty features object for invalid input
 */
function createEmptyFeatures(): TextFeatures {
  return {
    entropies: { shannon: 0, normalized: 0, wordLevel: 0 },
    complexity: { readability: 0, sentenceVariability: 0, vocabRichness: 0 },
    patterns: { repetition: 0, formulaic: 0, transition: 0 },
    metadata: { wordCount: 0, sentenceCount: 0, paragraphCount: 0 }
  };
}

/**
 * Calculate word frequencies
 */
function getWordFrequencies(words: string[]): Map<string, number> {
  const frequencies = new Map<string, number>();
  for (const word of words) {
    frequencies.set(word, (frequencies.get(word) || 0) + 1);
  }
  return frequencies;
}

/**
 * Calculate word-level entropy
 */
function calculateWordEntropy(frequencies: Map<string, number>, totalWords: number): number {
  let entropy = 0;
  for (const count of frequencies.values()) {
    const probability = count / totalWords;
    entropy -= probability * Math.log2(probability);
  }
  return entropy;
}

/**
 * Calculate variability for an array of numbers
 */
function calculateVariability(values: number[]): number {
  if (values.length <= 1) return 0;
  
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  
  // Return coefficient of variation (normalized std dev)
  return stdDev / (mean || 1);
}

/**
 * Detect repetition patterns in text
 * @returns Score between 0-1, higher means more repetition
 */
function detectRepetitionPatterns(text: string): number {
  // Simplified implementation focusing on repeated phrases
  const words = text.toLowerCase().split(/\s+/).filter(w => w.length > 0);
  if (words.length < 10) return 0;
  
  const threegrams = [];
  for (let i = 0; i < words.length - 2; i++) {
    threegrams.push(`${words[i]} ${words[i+1]} ${words[i+2]}`);
  }
  
  // Count three-gram occurrences
  const threegramCounts = new Map<string, number>();
  for (const threegram of threegrams) {
    threegramCounts.set(threegram, (threegramCounts.get(threegram) || 0) + 1);
  }
  
  // Count repeated three-grams
  let repeatedCount = 0;
  for (const count of threegramCounts.values()) {
    if (count > 1) repeatedCount += (count - 1);
  }
  
  // Calculate repetition score
  return Math.min(1, repeatedCount / (threegrams.length * 0.5));
}

/**
 * Detect formulaic phrases in text
 * @returns Score between 0-1, higher means more formulaic content
 */
function detectFormulaicPhrases(text: string): number {
  const formulaicPhrases = [
    "in conclusion", "to summarize", "as we can see", 
    "as mentioned earlier", "on the one hand", "on the other hand",
    "in this essay", "firstly", "secondly", "thirdly", "finally",
    "it is important to note", "it should be noted", "it is worth noting",
    "this demonstrates", "this shows", "this proves", "this indicates",
    "without a doubt", "certainly", "undoubtedly", "obviously",
    "in my opinion", "from my perspective", "as an AI"
  ];
  
  const lowerText = text.toLowerCase();
  let matchCount = 0;
  
  for (const phrase of formulaicPhrases) {
    if (lowerText.includes(phrase)) {
      matchCount++;
    }
  }
  
  return Math.min(1, matchCount / 10);
}

/**
 * Analyze transition words in text
 * @returns Score between 0-1, higher means more structured transitions
 */
function analyzeTransitionWords(text: string): number {
  const transitionWords = [
    "however", "therefore", "thus", "consequently", "furthermore",
    "moreover", "nevertheless", "nonetheless", "additionally", 
    "subsequently", "meanwhile", "conversely", "similarly",
    "likewise", "instead", "accordingly", "actually", "indeed"
  ];
  
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  let transitionCount = 0;
  
  for (const sentence of sentences) {
    const lowerSentence = sentence.toLowerCase();
    for (const word of transitionWords) {
      if (lowerSentence.includes(word)) {
        transitionCount++;
        break; // Count at most one transition word per sentence
      }
    }
  }
  
  return Math.min(1, transitionCount / Math.max(1, sentences.length));
}
